import React, { useMemo } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import AppIcon from '../common/AppIcon';
import StatusChip from '../common/StatusChip';
import PriorityChip from '../common/PriorityChip';
import BudgetBar from '../common/BudgetBar';
import { Topic } from '../../services/topics';
import { Task } from '../../services/tasks';
import { Planner } from '../../services/planners';
import { Document, downloadDocument } from '../../services/documents';
import { formatDate } from '../../utils/formatDate';
import confetti from 'canvas-confetti';
import { useCurrency } from '../../context/CurrencyContext';

interface TopicDetailProps {
  topic: Topic;
  tasks: Task[];
  documents: Document[];
  planners: Planner[];
  onEdit: () => void;
  onAddTask: () => void;
  onEditTask: (task: Task) => void;
  onStatusChange: (task: Task, status: string) => void;
}

const fireConfetti = (x: number, y: number) => {
  confetti({
    particleCount: 80,
    spread: 60,
    origin: { x: x / window.innerWidth, y: y / window.innerHeight },
    colors: ['#B07A8F', '#C9A96E', '#6B9E78', '#6A8EB0', '#D4A84E'],
  });
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const TopicDetail: React.FC<TopicDetailProps> = ({
  topic, tasks, documents, planners, onEdit, onAddTask, onEditTask, onStatusChange,
}) => {
  const { symbol: currencySymbol } = useCurrency();
  const planner = planners.find((p) => p.id === topic.assigned_planner_id);
  const totalBudget = tasks.reduce((sum, t) => sum + (t.budget_actual ?? t.budget_expected ?? 0), 0);
  const totalPaid = tasks.reduce((sum, t) => sum + (t.budget_paid ?? 0), 0);
  const hasBudget = totalBudget > 0;
  const completedCount = tasks.filter((t) => t.status === 'completed').length;

  const handleCompleteTask = (task: Task, e: React.MouseEvent) => {
    e.stopPropagation();
    const x = e.clientX;
    const y = e.clientY;
    onStatusChange(task, 'completed');
    if (task.status !== 'completed') {
      setTimeout(() => fireConfetti(x, y), 200);
    }
  };

  const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      // Completed tasks go to the bottom
      if (a.status === 'completed' && b.status !== 'completed') return 1;
      if (a.status !== 'completed' && b.status === 'completed') return -1;
      // Then by due date (earliest first, no date last)
      if (a.due_date && b.due_date) {
        const dateCmp = a.due_date.localeCompare(b.due_date);
        if (dateCmp !== 0) return dateCmp;
      } else if (a.due_date && !b.due_date) return -1;
      else if (!a.due_date && b.due_date) return 1;
      // Then by priority
      return (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1);
    });
  }, [tasks]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <Box sx={{ p: 3, height: '100%', overflowY: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {topic.emoji && <span style={{ marginRight: 8 }}>{topic.emoji}</span>}
          {topic.name}
        </Typography>
        <Button startIcon={<AppIcon name="edit" />} onClick={onEdit} variant="outlined" size="small">
          Edit
        </Button>
      </Box>

      {/* Status and planner */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
        <StatusChip status={topic.status} />
        {topic.deadline && (
          <Typography variant="body2" color="text.secondary">
            Due {formatDate(topic.deadline)}
          </Typography>
        )}
        {planner && (
          <Chip
            label={planner.name}
            size="small"
            variant="outlined"
            avatar={
              <Avatar sx={{ bgcolor: planner.color || 'grey.400', width: 20, height: 20 }}>
                <AppIcon name="person" sx={{ fontSize: 14, color: 'white' }} />
              </Avatar>
            }
            sx={{
              ml: 'auto',
              color: planner.color || 'grey.800',
              borderColor: planner.color || '#E4DED8',
              fontWeight: 500,
            }}
          />
        )}
      </Box>

      {/* Notes */}
      {topic.notes && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{topic.notes}</Typography>
      )}

      {/* Budget */}
      {hasBudget && (
        <Box sx={{ mb: 3 }}>
          <BudgetBar
            expected={totalBudget}
            paid={totalPaid}
          />
        </Box>
      )}

      {/* Tasks section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Tasks ({completedCount}/{tasks.length})
        </Typography>
        <Button startIcon={<AppIcon name="add" />} onClick={onAddTask} size="small">
          Add Task
        </Button>
      </Box>

      <List disablePadding>
        {tasks.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No tasks yet
          </Typography>
        )}
        {sortedTasks.map((task) => {
          const isCompleted = task.status === 'completed';
          const isOverdue = !isCompleted && task.due_date && new Date(task.due_date) < today;
          const exceedsDeadline = topic.deadline && task.due_date && task.due_date > topic.deadline;
          return (
            <ListItem
              key={task.id}
              onClick={() => onEditTask(task)}
              sx={{
                bgcolor: isCompleted ? 'rgba(107,158,120,0.04)' : 'grey.50',
                borderRadius: 2,
                mb: 0.5,
                border: '1px solid',
                borderColor: isCompleted ? 'rgba(107,158,120,0.2)' : 'grey.200',
                cursor: 'pointer',
                opacity: isCompleted ? 0.55 : 1,
                py: 1,
                px: 1.5,
                gap: 1,
                '&:hover': { bgcolor: isCompleted ? 'rgba(107,158,120,0.08)' : 'grey.100' },
                '&:hover .complete-btn-wrap': { width: 34 },
              }}
            >
              {isCompleted ? (
                <AppIcon name="check-circle" fontSize="small" sx={{ color: '#6B9E78', mr: 0.5 }} />
              ) : (
                <Box
                  className="complete-btn-wrap"
                  sx={{
                    width: 0,
                    overflow: 'hidden',
                    transition: 'width 0.2s',
                    display: 'flex',
                    flexShrink: 0,
                  }}
                >
                  <Tooltip title="Mark as Done">
                    <IconButton
                      size="small"
                      onClick={(e) => handleCompleteTask(task, e)}
                      sx={{ color: 'success.main', mr: 0.5 }}
                    >
                      <AppIcon name="check-circle" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        textDecoration: isCompleted ? 'line-through' : 'none',
                        color: isCompleted ? 'text.secondary' : 'text.primary',
                        flexGrow: 1,
                      }}
                    >
                      {task.title}
                    </Typography>
                    {exceedsDeadline && !isCompleted && (
                      <Tooltip title="Task due date exceeds topic deadline">
                        <span style={{ fontSize: 14 }}>⚠️</span>
                      </Tooltip>
                    )}
                  </Box>
                }
                secondary={
                  (task.due_date || task.budget_actual != null || task.budget_expected != null) ? (
                    <Box component="span" sx={{ display: 'flex', gap: 1 }}>
                      {task.due_date && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ color: isOverdue ? 'error.main' : 'text.secondary' }}
                        >
                          {formatDate(task.due_date)}
                        </Typography>
                      )}
                      {(task.budget_actual != null || task.budget_expected != null) && (
                        <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                          {currencySymbol}{(task.budget_actual ?? task.budget_expected ?? 0).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  ) : null
                }
                sx={{ m: 0 }}
              />
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                {!isCompleted && <PriorityChip priority={task.priority} />}
              </Box>
            </ListItem>
          );
        })}
      </List>

      {/* Documents section */}
      {documents.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            Documents ({documents.length})
          </Typography>
          <List disablePadding>
            {documents.map((doc) => (
              <ListItem
                key={doc.id}
                onClick={async () => {
                  try {
                    const res = await downloadDocument(doc.id);
                    const blob = new Blob([res.data]);
                    const url = window.URL.createObjectURL(blob);
                    const a = window.document.createElement('a');
                    a.href = url;
                    a.download = doc.filename;
                    a.click();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    console.error('Download failed', err);
                  }
                }}
                sx={{
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  mb: 0.5,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  cursor: 'pointer',
                  py: 0.75,
                  px: 1.5,
                  '&:hover': { bgcolor: 'grey.100' },
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {doc.filename}
                    </Typography>
                  }
                  secondary={formatSize(doc.size_bytes)}
                  sx={{ m: 0 }}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );
};

export default TopicDetail;
